import React, { createContext, useContext, useEffect, useState } from 'react'
import { JobRole } from '../../../types/Employee/JobRole'
import { ProjectFeature } from '../../../types/Project/ProjectFeature'
import {
	getProjectRoles,
	getProjectFeatures,
	getProjectTimeZone,
	getProjectCrafts,
	getProjectTemplate,
} from '../../../services/projectService'
import { useTokenClaims } from '../../../security/useTokenClaims'
import { useAuthContext } from '../../../security/AuthContext'
import { Craft } from '../../../types/Project/Craft'

interface ProjectFeaturesContextType {
	projectRoles: JobRole[] | undefined
	projectFeatures: ProjectFeature[] | undefined
	projectTimeZone: string | undefined
	projectTitle: string | undefined
	isFeatureEnabled: (featureId: number) => boolean
	toggleFeatureContext: (featureId: number, enabled: boolean) => void
	refreshProjectRoles: () => void
	projectCrafts: Craft[] | undefined
	projectTemplate: number | undefined
	projectCustomer: string | undefined
	/** False until the initial project features fetch finishes (or is skipped when no project token). */
	featuresReady: boolean
}

const ProjectFeaturesContext = createContext<ProjectFeaturesContextType | null>(null)

const useProjectFeaturesContext = (): ProjectFeaturesContextType => {
	const projectFeaturesContextState = useContext(ProjectFeaturesContext)
	if (projectFeaturesContextState === null) {
		throw new Error(
			'ProjectFeaturesContext not found. Try wrapping a parent component with <ProjectFeaturesContext.Provider>.',
		)
	}
	return projectFeaturesContextState
}

type ProjectFeaturesProviderProps = {
	children?: React.ReactNode
}

const ProjectFeaturesProvider = ({ children }: ProjectFeaturesProviderProps) => {
	const [projectRoles, setProjectRoles] = useState<JobRole[]>()
	const [projectFeatures, setProjectFeatures] = useState<ProjectFeature[]>()
	const [projectTimeZone, setProjectTimeZone] = useState<string>()
	const [projectCrafts, setProjectCrafts] = useState<Craft[] | undefined>()
	const [projectTitle, setProjectTitle] = useState<string>()
	const [projectTemplate, setProjectTemplate] = useState<number>()
	const [projectCustomer, setProjectCustomer] = useState<string>()
	const [featuresReady, setFeaturesReady] = useState(false)
	const { token, hasWpaToken } = useAuthContext()
	useEffect(() => {
		if (!hasWpaToken()) {
			setFeaturesReady(false)
			return
		}

		setFeaturesReady(false)
		;(async () => {
			try {
				const { projectId } = useTokenClaims()
				const [roles, features, timeZone, crafts, template] = await Promise.all([
					getProjectRoles(projectId),
					getProjectFeatures(),
					getProjectTimeZone(),
					getProjectCrafts(),
					getProjectTemplate(),
				])
				setProjectRoles(roles)
				setProjectFeatures(features)
				setProjectTimeZone(timeZone.timeZone)
				setProjectTitle(timeZone.projectTitle)
				setProjectCustomer(timeZone.customer)
				setProjectCrafts(crafts)
				setProjectTemplate(template)
			} catch (error) {
				console.error('Failed to fetch project features:', error)
			} finally {
				setFeaturesReady(true)
			}
		})()
	}, [token])

	const isFeatureEnabled = (featureId: number): boolean => {
		if (!projectFeatures) return false
		const feature = projectFeatures.find((f) => f.id === featureId)
		return feature?.enabled ?? false
	}

	const toggleFeatureContext = (featureId: number, enabled: boolean) => {
		if (!projectFeatures) return
		const feature = projectFeatures.find((f) => f.id === featureId)
		if (feature) {
			feature.enabled = enabled
			setProjectFeatures([...projectFeatures])
		}
	}

	const refreshProjectRoles = () => {
		const { projectId } = useTokenClaims()

		getProjectRoles(projectId).then(setProjectRoles)
	}

	return (
		<ProjectFeaturesContext.Provider
			value={{
				projectRoles,
				projectFeatures,
				projectTimeZone,
				projectTemplate,
				isFeatureEnabled,
				toggleFeatureContext,
				refreshProjectRoles,
				projectCrafts,
				projectTitle,
				projectCustomer,
				featuresReady,
			}}
		>
			{children}
		</ProjectFeaturesContext.Provider>
	)
}

export { useProjectFeaturesContext, ProjectFeaturesProvider }
